/**
 * Customer dev-docs API — maps legacy /doc URLs to /v1/customer/doc REST endpoints.
 */
import { callApi } from "./fetch";
import { categoryList } from "../components/application/developer-docs/common/constants";
import { productArnFromDocArn } from "../utils/devDocArnMatch";

type DevDocListPayload = {
  dev_docs?: unknown[];
  metadata?: { total?: number; offset?: number; limit?: number };
};

const LOG_PREFIX = "[customer-dev-docs]";
/** Matches SDS max page size and admin Edit Docs (`ADMIN_DOCS_LIST_LIMIT`). */
const CUSTOMER_DOC_LIST_LIMIT = 200;

const SIDEBAR_TO_DB_CATEGORY: Record<string, string> = Object.fromEntries(
  Object.entries(categoryList).map(([dbCategory, sidebarId]) => [
    sidebarId,
    dbCategory,
  ])
);

type OpenApiField = {
  name: string;
  example: unknown;
  required: boolean;
  description: string;
};

function normalizeOpenApiFieldList(raw: unknown): OpenApiField[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const record = (entry ?? {}) as Record<string, unknown>;
      const name = String(record.name ?? record.param_name ?? "").trim();
      return {
        name,
        example: record.example ?? "",
        required: Boolean(record.required ?? record.param_required ?? false),
        description: String(record.description ?? record.param_desc ?? ""),
      };
    })
    .filter((field) => field.name.length > 0);
}

function normalizeDevDocItem(item: unknown): Record<string, unknown> {
  if (!item || typeof item !== "object") return {};
  const doc = item as Record<string, unknown>;
  const curl =
    typeof doc.curl === "string" ? doc.curl : String(doc.curl ?? "");
  return {
    ...doc,
    _id: doc._id ?? doc.dev_doc_id,
    arn: doc.arn,
    type: doc.type,
    name: doc.name,
    category: doc.category,
    description: doc.description,
    curl,
    params: normalizeOpenApiFieldList(doc.params),
    headers: normalizeOpenApiFieldList(doc.headers),
    responses: doc.responses ?? {},
  };
}

function readListMetadata(data: unknown): DevDocListPayload["metadata"] | undefined {
  if (!data || typeof data !== "object" || Array.isArray(data)) return undefined;
  const metadata = (data as DevDocListPayload).metadata;
  return metadata && typeof metadata === "object" ? metadata : undefined;
}

function summarizeDevDocs(docs: Record<string, unknown>[]) {
  const byCategory: Record<string, number> = {};
  const byType: Record<string, number> = {};
  const arns: string[] = [];

  docs.forEach((doc) => {
    const category = String(doc.category ?? "unknown");
    const type = String(doc.type ?? "unknown");
    const arn = String(doc.arn ?? "");
    byCategory[category] = (byCategory[category] ?? 0) + 1;
    byType[type] = (byType[type] ?? 0) + 1;
    if (arn) arns.push(arn);
  });

  return { count: docs.length, byCategory, byType, arns };
}

function logDevDocFetch(
  path: string,
  rawData: unknown,
  docs: Record<string, unknown>[]
) {
  const metadata = readListMetadata(rawData);
  const summary = summarizeDevDocs(docs);

  console.info(`${LOG_PREFIX} GET ${path}`, {
    responseShape: Array.isArray(rawData)
      ? "array"
      : rawData && typeof rawData === "object"
        ? Object.keys(rawData as object)
        : typeof rawData,
    metadata,
    receivedCount: summary.count,
    byCategory: summary.byCategory,
    byType: summary.byType,
    arns: summary.arns,
  });

  const total = metadata?.total;
  if (typeof total === "number" && total > summary.count) {
    console.warn(
      `${LOG_PREFIX} API metadata.total (${total}) exceeds dev_docs returned (${summary.count}). ` +
        `Default server limit may be truncating subscribed docs (metadata.limit=${metadata?.limit ?? "?"}, offset=${metadata?.offset ?? "?"}).`
    );
  }

  if (summary.count === 0) {
    console.warn(
      `${LOG_PREFIX} No dev docs in response — org may have no subscribed products with dev-doc rows, or the session org may differ from expected.`
    );
  }
}

function unwrapDevDocList(data: unknown): Record<string, unknown>[] {
  let docs: unknown[] = [];
  if (Array.isArray(data)) {
    docs = data;
  } else if (data && typeof data === "object" && "dev_docs" in data) {
    const listed = (data as DevDocListPayload).dev_docs;
    docs = Array.isArray(listed) ? listed : [];
  } else if (data != null) {
    console.warn(`${LOG_PREFIX} Unexpected /doc payload shape`, data);
  }
  return docs.map(normalizeDevDocItem);
}

function toLegacyParams(params: unknown): Record<string, unknown>[] {
  if (!Array.isArray(params)) return [];
  return params.map((param) => {
    const record = (param ?? {}) as Record<string, unknown>;
    return {
      param_name: record.param_name ?? record.name ?? "",
      param_required: record.param_required ?? record.required ?? false,
      param_desc: record.param_desc ?? record.description ?? "",
    };
  });
}

function extractDocResponse(responses: unknown): string {
  if (responses == null) return "{}";
  let parsed = responses;
  if (typeof responses === "string") {
    try {
      parsed = JSON.parse(responses);
    } catch {
      return responses;
    }
  }
  if (typeof parsed !== "object" || parsed === null) {
    return String(parsed);
  }
  const record = parsed as Record<string, unknown>;
  const success = record["200"] ?? record[200];
  if (success && typeof success === "object") {
    const inner = success as Record<string, unknown>;
    const payload = inner["100"] ?? inner[100] ?? Object.values(inner)[0];
    if (payload === undefined) return JSON.stringify(success);
    return typeof payload === "string" ? payload : JSON.stringify(payload);
  }
  return JSON.stringify(parsed);
}

function toLegacyDeveloperDocItem(doc: Record<string, unknown>) {
  return {
    doc_arn: doc.arn,
    doc_name: doc.name,
    doc_desc: doc.description,
    doc_curl: doc.curl,
    doc_response: extractDocResponse(doc.responses),
    doc_params: toLegacyParams(doc.params),
    category: doc.category,
    note: doc.note,
  };
}

function groupIdForType(type: unknown): string {
  const normalized = String(type ?? "unknown").trim().toLowerCase();
  if (normalized === "telecom") return "telecom";
  return normalized.split(/\s+/)[0] || "unknown";
}

function baseGroupArn(arn: unknown): string {
  return productArnFromDocArn(String(arn ?? ""));
}

function groupDocsByType(docs: Record<string, unknown>[]) {
  const groups = new Map<string, { _id: string; doc_arn: string; docs: unknown[] }>();

  docs.forEach((doc) => {
    const groupId = groupIdForType(doc.type);
    if (!groups.has(groupId)) {
      groups.set(groupId, {
        _id: groupId,
        doc_arn: baseGroupArn(doc.arn),
        docs: [],
      });
    }
    groups.get(groupId)?.docs.push(toLegacyDeveloperDocItem(doc));
  });

  return Array.from(groups.values());
}

async function fetchDevDocs(category?: string): Promise<Record<string, unknown>[]> {
  const baseParams = new URLSearchParams();
  baseParams.set("limit", String(CUSTOMER_DOC_LIST_LIMIT));
  if (category) {
    const dbCategory = SIDEBAR_TO_DB_CATEGORY[category] ?? category;
    baseParams.set("category", dbCategory);
  }

  console.info(`${LOG_PREFIX} fetchDevDocs start`, {
    category: category ?? "(all)",
    limit: CUSTOMER_DOC_LIST_LIMIT,
  });

  const allDocs: Record<string, unknown>[] = [];
  let offset = 0;
  let lastRawData: unknown = null;
  let lastPath = "/doc";

  try {
    while (true) {
      const params = new URLSearchParams(baseParams);
      if (offset > 0) {
        params.set("offset", String(offset));
      }
      const query = params.toString();
      lastPath = query ? `/doc?${query}` : "/doc";

      const data = await callApi(lastPath, { method: "GET" });
      lastRawData = data;
      const page = unwrapDevDocList(data);
      allDocs.push(...page);

      const metadata = readListMetadata(data);
      const total = metadata?.total;
      if (
        page.length === 0 ||
        typeof total !== "number" ||
        allDocs.length >= total ||
        page.length < CUSTOMER_DOC_LIST_LIMIT
      ) {
        break;
      }
      offset += page.length;
    }

    logDevDocFetch(lastPath, lastRawData, allDocs);
    return allDocs;
  } catch (error) {
    console.error(`${LOG_PREFIX} fetchDevDocs failed`, { path: lastPath, category, error });
    throw error;
  }
}

/** Legacy GET /doc — flat array for Redoc OpenAPI builder. */
export async function fetchAllCustomerDocs(): Promise<Record<string, unknown>[]> {
  return fetchDevDocs();
}

/** Legacy GET /doc/:category — grouped docs for developer-docs product cards. */
export async function fetchCustomerDocsByCategory(
  category: string
): Promise<{ _id: string; doc_arn: string; docs: unknown[] }[]> {
  const docs = await fetchDevDocs(category);
  const grouped = groupDocsByType(docs);
  console.info(`${LOG_PREFIX} fetchCustomerDocsByCategory`, {
    category,
    flatCount: docs.length,
    groupCount: grouped.length,
    groups: grouped.map((g) => ({
      id: g._id,
      doc_arn: g.doc_arn,
      variantCount: g.docs.length,
    })),
  });
  return grouped;
}
